import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSupplyRequisitions } from '../../supplies/requisitions/useData';
import { useSupplierQuotations } from '../../supplies/supplier-quotations/useData';
import { useSupplyPurchaseOrders } from '../../supplies/supply-purchase-orders/useData';
import { useSupplyDeliveries } from '../../supplies/supply-deliveries/useData';
import { useSupplyInvoices } from '../../supplies/supply-invoices/useData';

interface CounterItem {
  key: string;
  label: string;
  value: number;
  href: string;
  accent: string;
  icon: React.ReactNode;
}

const ScmsPipelineCounters: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { total: requisitionsTotal, isLoading: reqLoading } = useSupplyRequisitions(undefined, 1, 1);
  const { total: quotationsTotal, isLoading: quoLoading } = useSupplierQuotations(undefined, 1, 1);
  const { total: ordersTotal, isLoading: poLoading } = useSupplyPurchaseOrders(undefined, 1, 1);
  const { total: deliveriesTotal, isLoading: delLoading } = useSupplyDeliveries(undefined, 1, 1);
  const { total: invoicesTotal, isLoading: invLoading } = useSupplyInvoices(undefined, 1, 1);

  const isLoading = reqLoading || quoLoading || poLoading || delLoading || invLoading;
  const counters: CounterItem[] = [
    {
      key: 'req',
      label: 'Supply Requisition',
      value: requisitionsTotal ?? 0,
      href: '/supplies/requisitions',
      accent: colors.primary,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h10M9 9h10M9 13h10M5 5h.01M5 9h.01M5 13h.01M4 19h16" />
        </svg>
      ),
    },
    {
      key: 'quo',
      label: 'Quotations',
      value: quotationsTotal ?? 0,
      href: '/supplies/supplier-quotations',
      accent: colors.accent,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8M8 15h5M7 3h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V5a2 2 0 012-2z" />
        </svg>
      ),
    },
    {
      key: 'po',
      label: 'Purchase Orders',
      value: ordersTotal ?? 0,
      href: '/supplies/supply-purchase-orders',
      accent: colors.primary,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l9-4 9 4-9 4-9-4zm0 5l9 4 9-4m-18 5l9 4 9-4" />
        </svg>
      ),
    },
    {
      key: 'dlv',
      label: 'Deliveries',
      value: deliveriesTotal ?? 0,
      href: '/supplies/supply-deliveries',
      accent: 'bg-emerald-500',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17h6m-9 0a2 2 0 11-4 0 2 2 0 014 0zm16 0a2 2 0 11-4 0 2 2 0 014 0zM5 17V9h9v8M14 11h4l2 3v3h-6v-6z" />
        </svg>
      ),
    },
    {
      key: 'inv',
      label: 'Invoices',
      value: invoicesTotal ?? 0,
      href: '/supplies/supply-invoices',
      accent: 'bg-[#6b8fb8]',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2zm2 4h6m-6 4h6m-6 4h4" />
        </svg>
      ),
    },
  ];
  const totalPipeline = counters.reduce((sum, item) => sum + item.value, 0);

  const cardClass = embedded
    ? 'min-w-0 flex-1 flex flex-col min-h-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-base font-semibold ${colors.text}`}>Supplies · Pipeline</h2>
        {!isLoading && (
          <div className={`rounded-full border px-3 py-1 text-xs font-medium ${colors.border} ${colors.textMuted}`}>
            Total: <span className={colors.text}>{totalPipeline}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2.5">
        {isLoading
          ? Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} className={`h-24 rounded-xl ${colors.surfaceHover} animate-pulse`} />
            ))
          : counters.map((item) => (
              <button
                type="button"
                key={item.key}
                onClick={() => navigate(item.href)}
                className={`group rounded-xl border p-3 text-left transition-all ${colors.border} ${colors.interactiveHover} hover:-translate-y-[1px]`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${colors.textMuted}`}>{item.label}</span>
                  <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-white ${item.accent}`}>
                    {item.icon}
                  </span>
                </div>
                <div className={`mt-2 text-2xl font-semibold tracking-tight ${colors.text}`}>{item.value}</div>
                <div className={`mt-2 h-1.5 w-full rounded-full ${colors.surfaceHover}`}>
                  <div className={`h-1.5 rounded-full ${item.accent}`} style={{ width: `${Math.min(100, totalPipeline > 0 ? (item.value / totalPipeline) * 100 : 0)}%` }} />
                </div>
              </button>
            ))}
      </div>
    </div>
  );
};

export default ScmsPipelineCounters;
